import React, {
  Dispatch,
  FC,
  SetStateAction,
  createContext,
  useContext,
  useState,
  useMemo,
  useCallback,
  PropsWithChildren,
} from 'react';
import { initialActiveCategory } from '../config';
import { NavigationSection, NavigationCategory, FilterValues, FiltersCounts } from '../types';
import { getNestedEntity } from '../utils';

type DrawerProviderType = {
  isInitiallyDrawerOpened?: boolean;
  initialNavigationSections?: NavigationSection[];
};

type DrawerContextType = {
  isDrawerOpened: boolean;
  isLoading: boolean;
  isCancelButtonClicked: boolean;
  isSearching: boolean;
  searchValue: string;
  activeCategory: NavigationCategory;
  activeWithSelectionCategories: NavigationCategory[];
  navigationSections: NavigationSection[];
  navigationCategories: NavigationCategory[];
  retainedFilters: FilterValues;
  filtersCounts: FiltersCounts;
  setDrawerOpened: Dispatch<SetStateAction<boolean>>;
  setLoading: Dispatch<SetStateAction<boolean>>;
  setCancelButtonClicked: Dispatch<SetStateAction<boolean>>;
  setSearchValue: Dispatch<SetStateAction<string>>;
  setActiveCategory: Dispatch<SetStateAction<NavigationCategory | null>>;
  setActiveWithSelectionCategories: Dispatch<SetStateAction<NavigationCategory[]>>;
  setNavigationSections: Dispatch<SetStateAction<NavigationSection[]>>;
  setRetainedFilters: Dispatch<SetStateAction<FilterValues>>;
  setFiltersCounts: Dispatch<SetStateAction<FiltersCounts>>;
  resetDrawerContext: () => void;
};

const DrawerContext = createContext<DrawerContextType>({
  isDrawerOpened: false,
  isLoading: false,
  isCancelButtonClicked: false,
  isSearching: false,
  searchValue: '',
  activeCategory: initialActiveCategory,
  activeWithSelectionCategories: [],
  navigationSections: [],
  navigationCategories: [],
  retainedFilters: null,
  filtersCounts: null,
  setDrawerOpened: () => undefined,
  setLoading: () => undefined,
  setCancelButtonClicked: () => undefined,
  setSearchValue: () => undefined,
  setActiveCategory: () => undefined,
  setActiveWithSelectionCategories: () => undefined,
  setNavigationSections: () => undefined,
  setRetainedFilters: () => undefined,
  setFiltersCounts: () => undefined,
  resetDrawerContext: () => undefined,
});

export const useDrawerContext = (): DrawerContextType => {
  return useContext(DrawerContext);
};

export const DrawerProvider: FC<PropsWithChildren<DrawerProviderType>> = ({
  isInitiallyDrawerOpened = false,
  initialNavigationSections = [],
  children,
}) => {
  const [isDrawerOpened, setDrawerOpened] = useState<DrawerContextType['isDrawerOpened']>(isInitiallyDrawerOpened);
  const [isLoading, setLoading] = useState<DrawerContextType['isDrawerOpened']>(false);
  const [isCancelButtonClicked, setCancelButtonClicked] = useState<DrawerContextType['isCancelButtonClicked']>(false);
  const [searchValue, setSearchValue] = useState<DrawerContextType['searchValue']>('');
  const [activeCategory, setActiveCategory] = useState<DrawerContextType['activeCategory']>(initialActiveCategory);
  const [activeWithSelectionCategories, setActiveWithSelectionCategories] = useState<
    DrawerContextType['activeWithSelectionCategories']
  >([]);
  const [navigationSections, setNavigationSections] =
    useState<DrawerContextType['navigationSections']>(initialNavigationSections);
  const [retainedFilters, setRetainedFilters] = useState<DrawerContextType['retainedFilters']>(null);
  const [filtersCounts, setFiltersCounts] = useState<DrawerContextType['filtersCounts']>(null);

  const resetDrawerContext = useCallback(() => {
    setSearchValue('');
    setActiveCategory(initialActiveCategory);
  }, []);

  const navigationCategories = useMemo(() => {
    return getNestedEntity(navigationSections);
  }, [navigationSections]);

  const providerValue = useMemo(
    () => ({
      isDrawerOpened,
      isLoading,
      isCancelButtonClicked,
      isSearching: searchValue.length > 0,
      searchValue,
      activeCategory,
      activeWithSelectionCategories,
      navigationSections,
      navigationCategories,
      retainedFilters,
      filtersCounts,
      setDrawerOpened,
      setLoading,
      setCancelButtonClicked,
      setSearchValue,
      setActiveCategory,
      setActiveWithSelectionCategories,
      setNavigationSections,
      setRetainedFilters,
      setFiltersCounts,
      resetDrawerContext,
    }),
    [
      isDrawerOpened,
      isLoading,
      isCancelButtonClicked,
      searchValue,
      activeCategory,
      activeWithSelectionCategories,
      navigationSections,
      navigationCategories,
      retainedFilters,
      filtersCounts,
      resetDrawerContext,
    ]
  );

  return <DrawerContext.Provider value={providerValue}>{children}</DrawerContext.Provider>;
};
